// useKitchenMenu.js
import { useState, useCallback } from 'react';
import kitchenMenuApi from './kitchenMenuApi';

export const useKitchenMenu = (kindergartenId) => {
  const [day, setDay] = useState(null); // null = no menu saved yet for this date
  const [suggestedPortions, setSuggestedPortions] = useState({});
  const [sufficiency, setSufficiency] = useState(null); // null = not checked yet
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cooking, setCooking] = useState(false);
  const [error, setError] = useState(null);
  const [shortfalls, setShortfalls] = useState(null);

  const loadDay = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    setSufficiency(null);
    setShortfalls(null);
    try {
      const [dayResult, suggested] = await Promise.all([
        kitchenMenuApi.getDay(kindergartenId, date),
        kitchenMenuApi.suggestPortions(kindergartenId, date)
      ]);
      setDay(dayResult);
      setSuggestedPortions(suggested || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [kindergartenId]);

  const save = useCallback(async (date, ageGroups) => {
    setSaving(true);
    setError(null);
    try {
      const saved = await kitchenMenuApi.saveDay({ kindergartenId, date, ageGroups });
      setDay(saved);
      return saved;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [kindergartenId]);

  const checkSufficiency = useCallback(async (date) => {
    setError(null);
    try {
      const result = await kitchenMenuApi.checkSufficiency(kindergartenId, date);
      setSufficiency(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [kindergartenId]);

  const cook = useCallback(async (date) => {
    setCooking(true);
    setError(null);
    setShortfalls(null);
    try {
      const cooked = await kitchenMenuApi.cook(kindergartenId, date);
      setDay(cooked);
      return cooked;
    } catch (err) {
      if (Array.isArray(err.details)) {
        setShortfalls(err.details);
      } else {
        setError(err.message);
      }
      throw err;
    } finally {
      setCooking(false);
    }
  }, [kindergartenId]);

  return {
    day, suggestedPortions, sufficiency, shortfalls,
    loading, saving, cooking, error,
    actions: { loadDay, save, checkSufficiency, cook }
  };
};

export default useKitchenMenu;